import type { RouteLocationNormalized } from 'vue-router';

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
  const jwtCookie = useCookie('token');
  const refreshCookie = useCookie('refreshToken');
  const userStore = useUserStore();
  const config = useRuntimeConfig();
  const siteType = config.public.frontMode;

  function decodeJwt(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      let decodedPayload: string;
      if (process.server) {
        decodedPayload = Buffer.from(payload, 'base64').toString('utf8');
      } else {
        decodedPayload = atob(payload);
      }
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Erreur lors du décodage du JWT:', error);
      return null;
    }
  }

  async function resetAuthConfig() {
    try {
      await fetch('/api/auth/reset-config', { method: 'POST' });
      console.info('[Auth] Reset auth configuration.');
    } catch (error) {
      console.error('[Auth] Failed to reset auth config:', error);
    }
  }

  async function checkAuthOnServer() {
    const validateJwt = (token: string) => {
      if (!token) return false;
      const decoded = decodeJwt(token);
      return decoded?.exp && Date.now() / 1000 < decoded.exp;
    };

    if (jwtCookie.value && validateJwt(jwtCookie.value)) return true;

    if (refreshCookie.value && !validateJwt(refreshCookie.value)) {
      //await resetAuthConfig();
      return false;
    }

    try {
      const { data } = await useFetch<{
        isAuthenticated: boolean;
        token?: string;
        refreshToken?: string;
      }>('/api/auth/refresh', {
        method: 'POST',
        body: {
          jwt: jwtCookie.value,
          refreshToken: refreshCookie.value,
        },
      });

      if (!data.value?.isAuthenticated || !data.value.token || !data.value.refreshToken) {
        await resetAuthConfig();
        return false;
      }

      // Update cookies with new tokens
      jwtCookie.value = data.value.token;
      refreshCookie.value = data.value.refreshToken;

      return true;
    } catch (error) {
      console.error('Error checking authentication:', error);
      await resetAuthConfig();
      return false;
    }
  }

  const isAuthenticated = await checkAuthOnServer();
  userStore.isAuthenticated = isAuthenticated;
  useCookie('isAuthenticated').value = isAuthenticated;
  const loginPath = '/auth/login';
  const isAuthPage = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
  ].includes(to.path);

  // Vérifier si c'est une page de commande avec token d'authentification
  const hasValidToken =
    to.query.token && typeof to.query.token === 'string' && to.query.token.length > 10;
  const isOrderPageWithToken = to.path.startsWith('/account/orders/') && hasValidToken;

  const homePath = '/';

  // Logique de redirection
  if (isAuthenticated) {
    // Si l'utilisateur est authentifié, il ne doit pas accéder aux pages d'authentification
    if (isAuthPage) {
      return navigateTo(homePath);
    } else {
      return; // Laisse passer la navigation
    }
  } else {
    // Si c'est une page de commande avec token, autoriser l'accès sans authentification
    if (isOrderPageWithToken) {
      console.log('Accès autorisé à la page de commande avec token:', to.path);
      return; // Laisse passer la navigation
    }

    if (siteType === 'private') {
      // Pour un site privé :
      // Si l'utilisateur non authentifié essaie d'accéder à une page autre que les pages d'authentification, rediriger vers /login
      if (!isAuthPage) {
        console.log('Redirection vers la page de connexion');
        return navigateTo(loginPath);
      }
    }
    if (siteType === 'public') {
      // Pour un site public :
      // Si l'utilisateur non authentifié essaie d'accéder à une page nécessitant une authentification, rediriger vers la page d'accueil
      if (to.meta.requiresAuth) {
        return navigateTo(homePath); //navigateTo(homePath);
      }
    }
    return; // Laisse passer la navigation
  }
});
